import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Loader2, 
  FileText, 
  Calendar, 
  LogOut,
  User,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  Lock,
  Mail,
  Phone,
  Building2,
  Sparkles,
  TrendingUp,
  Target,
  HelpCircle,
  MapPin,
  BookOpen,
  Gift
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { serviceList, formatPriceCents } from "@/content/serviceDescriptions";
import useServiceModal from "@/hooks/useServiceModal";
import ServiceInfoModal from "@/components/modals/ServiceInfoModal";
import EbookPromoModal from "@/components/modals/EbookPromoModal";
import { formatTime12Hour, formatDateMST } from "@/utils/timeUtils";
import { getMeetingTypeLabel, getMeetingTypeColor } from "@/constants/meetingTypes";
import MilestoneStatusPanel from "@/components/MilestoneStatusPanel";
import EvidencePanel from "@/components/EvidencePanel";
import AnalysisPanel from "@/components/AnalysisPanel";
import ReportPanel from "@/components/ReportPanel";
import ClosurePanel from "@/components/ClosurePanel";
import ServiceAProgressPanel from "@/components/ServiceAProgressPanel";
import { fetchLatestEngagement, trackEbookEvent, checkPricingEligibility } from "@/api/aimeansbusiness";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format price for display
const formatDynamicPrice = (price) => {
  if (!price) return "$--";
  if (price >= 1000) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `$${price}`;
};

const UserPortal = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [portalData, setPortalData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Service Modal State
  const { activeServiceId, isOpen: isServiceModalOpen, openServiceModal, closeServiceModal } = useServiceModal();
  const [showPurchaseCTA, setShowPurchaseCTA] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  
  // Ebook Promo Modal State
  const [showEbookModal, setShowEbookModal] = useState(false);
  const ebookOfferTracked = useRef(false);
  
  // User Info Modal State
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    telephone: "",
    business_name: "",
    // Address fields
    business_address: "",
    address_2: "",
    suite_number: "",
    city: "",
    state: "",
    zip_code: ""
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  // Active engagement state
  const [activeEngagement, setActiveEngagement] = useState(null);
  
  // Pricing eligibility state (gated until evaluation + SER)
  const [pricingEligible, setPricingEligible] = useState(false);
  
  // Dynamic pricing from admin config
  const [dynamicPrices, setDynamicPrices] = useState({});
  
  // Milestone completion tracking (for ReportPanel prerequisites)
  const [milestoneStatus, setMilestoneStatus] = useState({
    m4Complete: false,
    m5Complete: false,
    m6Complete: false
  });

  const fetchMilestones = async (serId) => {
    if (!serId || !token) return;
    try {
      const response = await axios.get(`${API}/engagements/${serId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const milestones = response.data?.milestones || [];
      setMilestoneStatus({
        m4Complete: milestones.some(m => m.milestone_id === "M4_SIGNAL_EXTRACTION_COMPLETE" && m.completed),
        m5Complete: milestones.some(m => m.milestone_id === "M5_ASSUMPTIONS_DECLARED" && m.completed),
        m6Complete: milestones.some(m => m.milestone_id === "M6_REPORT_ISSUED" && m.completed)
      });
    } catch (error) {
      console.error("Failed to fetch milestones:", error);
    }
  };

  const fetchPortalData = async () => {
    if (!token) {
      navigate("/login?redirect=/portal");
      return;
    }
    
    try {
      const [portalRes, appointmentsRes, engagementRes, pricingRes] = await Promise.all([
        axios.get(`${API}/user/portal`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          // Handle case where lead data doesn't exist (AIMB-A users)
          console.log("Portal data not found - user may be Service A only");
          return { data: {} };
        }),
        axios.get(`${API}/user/appointments`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        // Use fetchLatestEngagement to also get CLOSED engagements for report access
        fetchLatestEngagement().catch(() => ({ has_engagement: false })),
        // Fetch dynamic pricing from admin config
        axios.get(`${API}/public/service-prices`).catch(() => ({ data: {} }))
      ]);
      
      setPortalData(portalRes.data);
      setAppointments(appointmentsRes.data || []);
      
      // Set dynamic prices from admin config
      if (pricingRes.data) {
        setDynamicPrices(pricingRes.data);
      }
      
      // Set engagement if exists (including CLOSED engagements for report access)
      if (engagementRes.has_engagement && engagementRes.ser) {
        // Transform response to match existing component expectations
        setActiveEngagement({
          active: engagementRes.is_active,
          ser: engagementRes.ser,
          service: engagementRes.service
        });
        // Fetch milestone status for the engagement
        fetchMilestones(engagementRes.ser.id);
      }
      
      // Initialize profile form with user data
      setProfileForm({
        first_name: portalRes.data.first_name || "",
        last_name: portalRes.data.last_name || "",
        email: portalRes.data.email || "",
        telephone: portalRes.data.telephone || "",
        business_name: portalRes.data.business_name || "",
        // Address fields
        business_address: portalRes.data.business_address || "",
        address_2: portalRes.data.address_2 || "",
        suite_number: portalRes.data.suite_number || "",
        city: portalRes.data.city || "",
        state: portalRes.data.state || "",
        zip_code: portalRes.data.zip_code || ""
      });
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate("/login?redirect=/portal");
      } else {
        toast.error("Failed to load portal data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPortalData();
    
    // Check pricing eligibility
    const checkEligibility = async () => {
      try {
        const result = await checkPricingEligibility();
        setPricingEligible(result.pricing_eligible);
      } catch (err) {
        console.error("Failed to check pricing eligibility:", err);
        setPricingEligible(false);
      }
    };
    if (token) {
      checkEligibility();
    }
  }, [token, navigate, logout]);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Signed out successfully");
  };

  const handleRegenerateReport = async () => {
    if (!portalData?.lead_id) {
      toast.error("Unable to regenerate report - missing lead information");
      return;
    }

    setIsRegenerating(true);

    try {
      await axios.post(`${API}/user/regenerate-report`, {
        lead_id: portalData.lead_id,
        dev_override: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Report regenerated successfully!");
      await fetchPortalData();
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to regenerate report";
      toast.error(message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);

    try {
      await axios.put(`${API}/user/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
      await fetchPortalData();
      // Close modal on success
      setShowUserInfoModal(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update profile";
      toast.error(message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setIsChangingPassword(true);

    try {
      await axios.put(`${API}/user/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setShowPasswordSection(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handler for opening service modal with purchase intent
  const handleGetStarted = (serviceId) => {
    setShowPurchaseCTA(true);
    openServiceModal(serviceId);
  };

  // Handler for Learn More (no purchase CTA)
  const handleLearnMore = (serviceId) => {
    setShowPurchaseCTA(false);
    openServiceModal(serviceId);
  };

  // Handler for modal close
  const handleCloseServiceModal = () => {
    setShowPurchaseCTA(false);
    closeServiceModal();
  };

  // Map internal service IDs to public service IDs for checkout
  const SERVICE_ID_TO_PUBLIC = {
    "SERVICE_B": "AIMB-A",  // A.I. Readiness Consultation
    "SERVICE_A": "AIMB-B",  // Discovery & Viability Assessment
    "SERVICE_C": "AIMB-C"   // Strategic A.I. Roadmap
  };

  // Handler for purchase button - works for any service
  const handlePurchase = async (serviceId) => {
    setIsPurchasing(true);
    
    // Get the public service ID for the API
    const publicServiceId = SERVICE_ID_TO_PUBLIC[serviceId] || "AIMB-A";
    
    try {
      // Step 1: Create engagement/SER if not exists
      const createResponse = await axios.post(
        `${API}/engagements/start-service`,
        { service_id: publicServiceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const serId = createResponse.data.ser_id;
      
      // Step 2: Accept agreement (clicking "Proceed to Payment" = accepting terms)
      await axios.post(
        `${API}/ser/${serId}/accept-agreement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Step 3: Create Stripe checkout session
      const baseUrl = window.location.origin;
      const checkoutResponse = await axios.post(
        `${API}/ser/${serId}/service-a/stripe/checkout-session`,
        {
          success_url: `${baseUrl}/portal?ser_id=${serId}&payment=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${baseUrl}/portal?ser_id=${serId}&payment=cancelled`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Step 4: Redirect to Stripe
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      const message = error.response?.data?.detail || "Failed to start checkout. Please try again.";
      toast.error(message);
      setIsPurchasing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const reportDate = portalData?.report?.generated_at 
    ? new Date(portalData.report.generated_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : null;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2 flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/d89kgck4_A_I_Means_Business.jpg"
              alt="A.I. Means Business"
              className="h-12 sm:h-16 md:h-20 w-auto"
            />
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium truncate max-w-[150px] md:max-w-none">{portalData?.business_name || user?.business_name}</p>
              <p className="text-slate-400 text-xs truncate max-w-[150px] md:max-w-none">{user?.email}</p>
            </div>
            
            {/* User Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300 hover:text-white"
                  data-testid="user-menu-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Account
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-navy-900 border-slate-700">
                <DropdownMenuItem 
                  onClick={() => setShowUserInfoModal(true)}
                  className="text-slate-300 hover:text-white hover:bg-navy-800 cursor-pointer"
                  data-testid="user-info-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  User Info
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-slate-300 hover:text-white hover:bg-navy-800 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content - pt accounts for fixed header height */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pt-24 sm:pt-28 md:pt-32">
        {/* Page Title */}
        <div className="mb-4 sm:mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight break-words">
            A.I. Means Business
          </h1>
          <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-primary mt-1">
            User Portal
          </h2>
        </div>
        
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-10">
          <p className="font-heading text-lg sm:text-xl font-semibold text-white mb-1">
            Welcome back, {profileForm.first_name || 'valued partner'}
          </p>
          <p className="text-slate-400 text-sm sm:text-base">
            Your A.I. business advisory resources are ready below.
          </p>
        </div>

        {/* 1. Your A.I. Business Evaluation - Report Card */}
        {/* Only show if user has a lead_id (came through questionnaire flow) */}
        {portalData?.lead_id && (
          <div className="glass p-6 rounded-xl mb-6" data-testid="report-status-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-heading text-xl font-semibold text-white">
                    Your A.I. Business Evaluation
                  </h2>
                  {portalData?.report && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Complete
                    </span>
                  )}
                </div>
                
                {reportDate && (
                  <p className="text-slate-400 text-sm mb-3">
                    Generated: {reportDate}
                  </p>
                )}
                
                <p className="text-slate-300 text-sm mb-4">
                  Your personalized evaluation reveals key insights about your business&apos;s operational complexity,
                  decision readiness, and opportunities for strategic A.I. integration.
                </p>
                
                <Link to={`/evaluation/${portalData.lead_id}`}>
                  <Button 
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid="view-report-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View My Evaluation Report
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* 2. Continue Your A.I. Journey */}
        {/* Only show if user has a lead_id (came through questionnaire flow) */}
        {portalData?.lead_id && (
          <div className="glass p-6 rounded-xl mb-6" data-testid="next-steps-card">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-1">
                  Continue Your A.I. Journey
                </h3>
                <p className="text-slate-400 text-sm">
                  Your evaluation has uncovered meaningful opportunities
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Clarity Over Confusion</p>
                  <p className="text-slate-400 text-sm">
                    Based on your profile, a guided conversation can help you cut through the noise 
                    and focus on what actually matters for your specific situation—no more wasted 
                    time on solutions that don&apos;t fit.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Target className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Actionable Next Steps</p>
                  <p className="text-slate-400 text-sm">
                    Move from understanding to action. We&apos;ll translate your evaluation insights 
                    into a prioritized roadmap tailored to your business goals and operational reality.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Confident Decision-Making</p>
                  <p className="text-slate-400 text-sm">
                    Whether you&apos;re ready to move forward or still weighing options, you&apos;ll leave 
                    our conversation with the confidence to make the right decision for your business.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-700">
              <Link to={`/schedule/${portalData.lead_id}`}>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4"
                  data-testid="schedule-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule a Conversation
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* 3. Your Scheduled Appointments */}
        {appointments.length > 0 && (
          <div className="glass p-6 rounded-xl mb-6" data-testid="user-appointments-panel">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-1">
                  Your Scheduled Appointments
                </h3>
                <p className="text-slate-400 text-sm">
                  Upcoming sessions with your A.I. advisor
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {appointments.map((apt) => {
                const isPast = new Date(apt.date) < new Date();
                const statusStyles = {
                  scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
                  completed: "bg-green-500/20 text-green-400 border-green-500/30",
                  canceled: "bg-red-500/20 text-red-400 border-red-500/30",
                  no_show: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                };
                return (
                  <div 
                    key={apt.id}
                    className={`p-4 rounded-lg border ${isPast ? 'bg-navy-950/30 border-slate-800' : 'bg-navy-950/50 border-slate-700'}`}
                    data-testid={`appointment-${apt.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPast ? 'bg-slate-800' : 'bg-primary/20'}`}>
                          <Calendar className={`w-5 h-5 ${isPast ? 'text-slate-500' : 'text-primary'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isPast ? 'text-slate-500' : 'text-white'}`}>
                            {formatDateMST(apt.date)}
                          </p>
                          <p className={`text-sm ${isPast ? 'text-slate-600' : 'text-slate-400'}`}>
                            {formatTime12Hour(apt.time)} • {apt.duration || 30} minutes (Mountain Time)
                          </p>
                          <p className={`text-xs mt-1 ${isPast ? 'text-slate-600' : 'text-slate-500'}`}>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${getMeetingTypeColor(apt.meeting_type)}`}>
                              {getMeetingTypeLabel(apt.meeting_type)}
                            </span>
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${statusStyles[apt.status] || statusStyles.scheduled}`}>
                        {apt.status?.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 4. Available Services Panel - Canonical v1.0 (Pricing Gated) */}
        <div className="glass p-6 rounded-xl mb-6" data-testid="available-services-panel">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-white mb-1">
                Available Services
              </h3>
              <p className="text-slate-400 text-sm">
                Stand-alone decision-support engagements. No bundling or sequencing required.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {serviceList.map((service) => {
              // Get dynamic price from admin config
              const dynamicPrice = dynamicPrices[service.id];
              const priceDisplay = dynamicPrice ? formatDynamicPrice(dynamicPrice.price) : service.pricing.displayLabelWhenVisible.split(' ')[0];
              
              return (
              <div 
                key={service.id}
                className="p-4 rounded-lg bg-navy-950/50 border border-slate-800 hover:border-slate-700 transition-colors"
                data-testid={`portal-service-${service.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-white font-semibold text-sm">
                        {service.title}
                      </p>
                      {pricingEligible ? (
                        <span className="text-primary font-bold text-sm" data-testid={`portal-price-visible-${service.id}`}>
                          {priceDisplay}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-500 text-xs" data-testid={`portal-price-hidden-${service.id}`}>
                          <Lock className="w-3 h-3" />
                          Price revealed after evaluation
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs italic mb-2">
                      {service.decisionAnchor}
                    </p>
                    <p className="text-slate-400 text-xs">
                      {service.shortDescription}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLearnMore(service.id)}
                      className="text-primary hover:text-primary/80 text-xs whitespace-nowrap"
                      data-testid={`portal-service-info-${service.id}`}
                    >
                      Learn More
                    </Button>
                    {/* Purchase button - shown when pricing is visible */}
                    {pricingEligible && (
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-primary/90 text-white text-xs whitespace-nowrap"
                        onClick={() => handleGetStarted(service.id)}
                        data-testid={`purchase-service-${service.id}`}
                      >
                        Purchase
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>

        {/* Service A Progress Panel - TEMPORARILY HIDDEN
         *  "Your Progress" and "Your Documents" cards will be restored
         *  when we extend development to automate user services.
         */}
        {/* {activeEngagement?.ser && 
         (activeEngagement.ser.service_id === "AIMB-A" || 
          activeEngagement.ser.service_a_status) && (
          <div className="mb-6" data-testid="service-a-progress-section">
            <ServiceAProgressPanel serId={activeEngagement.ser.id} />
          </div>
        )} */}

        {/* Legacy Panels - For SERVICE_A (old BCO workflow) */}
        {/* Only show if NOT using new Service A workflow */}
        {activeEngagement?.ser && 
         activeEngagement.ser.service_id !== "AIMB-A" && 
         !activeEngagement.ser.service_a_status && (
          <>
            {/* Engagement Validation Status Panel */}
            <div className="mb-6" data-testid="engagement-status-section">
              <MilestoneStatusPanel serId={activeEngagement.ser.id} />
            </div>

            {/* Evidence Intake Panel - Show only if in EVIDENCE_COLLECTION state */}
            {activeEngagement.ser.lifecycle_state === "EVIDENCE_COLLECTION" && (
              <div className="mb-6" data-testid="evidence-intake-section">
                <EvidencePanel 
                  serId={activeEngagement.ser.id}
                  onEvidenceUpdate={() => {
                    fetchLatestEngagement().then(res => {
                      if (res.has_engagement && res.ser) {
                        setActiveEngagement({
                          active: res.is_active,
                          ser: res.ser,
                          service: res.service
                        });
                      }
                    }).catch(() => {});
                  }}
                />
              </div>
            )}

            {/* Analysis Panel - Show only if in ANALYSIS_SYNTHESIS state */}
            {activeEngagement.ser.lifecycle_state === "ANALYSIS_SYNTHESIS" && (
              <div className="mb-6" data-testid="analysis-section">
                <AnalysisPanel 
                  serId={activeEngagement.ser.id}
                  onAnalysisUpdate={() => {
                    fetchLatestEngagement().then(res => {
                      if (res.has_engagement && res.ser) {
                        setActiveEngagement({
                          active: res.is_active,
                          ser: res.ser,
                          service: res.service
                        });
                        fetchMilestones(res.ser.id);
                      }
                    }).catch(() => {});
                  }}
                />
              </div>
            )}

            {/* Report Panel - Show in ANALYSIS_SYNTHESIS, REPORT_ISSUED, or CLOSED states */}
            {(activeEngagement.ser.lifecycle_state === "ANALYSIS_SYNTHESIS" ||
              activeEngagement.ser.lifecycle_state === "REPORT_ISSUED" ||
              activeEngagement.ser.lifecycle_state === "CLOSED") && (
              <div className="mb-6" data-testid="report-generation-section">
                <ReportPanel 
                  serId={activeEngagement.ser.id}
                  m4Complete={milestoneStatus.m4Complete}
                  m5Complete={milestoneStatus.m5Complete}
                  lifecycleState={activeEngagement.ser.lifecycle_state}
                  onReportUpdate={() => {
                    fetchLatestEngagement().then(res => {
                      if (res.has_engagement && res.ser) {
                        setActiveEngagement({
                          active: res.is_active,
                          ser: res.ser,
                          service: res.service
                        });
                        fetchMilestones(res.ser.id);
                      }
                    }).catch(() => {});
                  }}
                />
              </div>
            )}

            {/* Closure Panel - Show only if in REPORT_ISSUED state */}
            {activeEngagement.ser.lifecycle_state === "REPORT_ISSUED" && (
              <div className="mb-6" data-testid="closure-section">
                <ClosurePanel 
                  serId={activeEngagement.ser.id}
                  m6Complete={milestoneStatus.m6Complete}
                  onClosureComplete={() => {
                    fetchLatestEngagement().then(res => {
                      if (res.has_engagement && res.ser) {
                        setActiveEngagement({
                          active: res.is_active,
                          ser: res.ser,
                          service: res.service
                        });
                        fetchMilestones(res.ser.id);
                      }
                    }).catch(() => {});
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Ebook Promotional CTA - Only shown after evaluation + onboarding completion */}
        {portalData?.report && activeEngagement?.ser && (
          <EbookOfferCard 
            serId={activeEngagement.ser.id}
            onOpenModal={() => setShowEbookModal(true)}
            onTrackOffer={async () => {
              if (!ebookOfferTracked.current) {
                ebookOfferTracked.current = true;
                try {
                  await trackEbookEvent("ebook_offer_shown", activeEngagement.ser.id);
                } catch (err) {
                  console.error("Failed to track ebook offer shown:", err);
                }
              }
            }}
          />
        )}

        {/* Dev Tools Card - Regenerate Report */}
        <div className="glass p-4 rounded-xl mb-6 border border-amber-500/30 bg-amber-500/5" data-testid="dev-tools-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <RefreshCw className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-400 text-xs font-medium uppercase tracking-wider">Development Tool</p>
                <p className="text-white text-sm font-medium">Regenerate Evaluation Report</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenerateReport}
              disabled={isRegenerating}
              className="border-amber-500/50 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              data-testid="regenerate-report-btn"
            >
              {isRegenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </>
              )}
            </Button>
          </div>
          <p className="text-slate-500 text-xs mt-2 ml-8 sm:ml-11">
            Bypasses the single-report limit. For testing report generation without completing a new questionnaire.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 sm:py-8 mt-8 sm:mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Link to="/portal" className="inline-block mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/d89kgck4_A_I_Means_Business.jpg"
              alt="A.I. Means Business"
              className="h-12 sm:h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-slate-500 text-xs px-2">
            Copyright © 2026, Use What You Got Publishing, LLC • www.AIMeansBusiness.net
          </p>
        </div>
      </footer>

      {/* User Info Modal */}
      <Dialog open={showUserInfoModal} onOpenChange={setShowUserInfoModal}>
        <DialogContent className="bg-navy-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              User Information
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View and update your profile and contact information
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
            {/* Profile Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-slate-300 flex items-center gap-2">
                  <User className="w-3 h-3" />
                  First Name
                </Label>
                <Input
                  id="first_name"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className="bg-navy-950 border-slate-700 text-white"
                  data-testid="profile-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-slate-300">Last Name</Label>
                <Input
                  id="last_name"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className="bg-navy-950 border-slate-700 text-white"
                  data-testid="profile-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_name" className="text-slate-300 flex items-center gap-2">
                <Building2 className="w-3 h-3" />
                Business Name
              </Label>
              <Input
                id="business_name"
                value={profileForm.business_name}
                onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                className="bg-navy-950 border-slate-700 text-white"
                data-testid="profile-business-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 flex items-center gap-2">
                <Mail className="w-3 h-3" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className="bg-navy-950 border-slate-700 text-white"
                data-testid="profile-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telephone" className="text-slate-300 flex items-center gap-2">
                <Phone className="w-3 h-3" />
                Telephone
              </Label>
              <Input
                id="telephone"
                type="tel"
                value={profileForm.telephone}
                onChange={(e) => setProfileForm({ ...profileForm, telephone: e.target.value })}
                className="bg-navy-950 border-slate-700 text-white"
                data-testid="profile-telephone"
              />
            </div>

            {/* Business Address Section */}
            <div className="border-t border-slate-700 pt-4 mt-4">
              <p className="text-slate-400 text-sm flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4" />
                Business Address
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business_address" className="text-slate-300">
                    Street Address
                  </Label>
                  <Input
                    id="business_address"
                    value={profileForm.business_address}
                    onChange={(e) => setProfileForm({ ...profileForm, business_address: e.target.value })}
                    placeholder="123 Main Street"
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="profile-business-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_2" className="text-slate-300">
                      Address Line 2
                    </Label>
                    <Input
                      id="address_2"
                      value={profileForm.address_2}
                      onChange={(e) => setProfileForm({ ...profileForm, address_2: e.target.value })}
                      placeholder="Building, Floor, etc."
                      className="bg-navy-950 border-slate-700 text-white"
                      data-testid="profile-address-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="suite_number" className="text-slate-300">
                      Suite/Unit
                    </Label>
                    <Input
                      id="suite_number"
                      value={profileForm.suite_number}
                      onChange={(e) => setProfileForm({ ...profileForm, suite_number: e.target.value })}
                      placeholder="Suite 100"
                      className="bg-navy-950 border-slate-700 text-white"
                      data-testid="profile-suite-number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-slate-300">
                      City
                    </Label>
                    <Input
                      id="city"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      placeholder="Denver"
                      className="bg-navy-950 border-slate-700 text-white"
                      data-testid="profile-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-slate-300">
                      State
                    </Label>
                    <Input
                      id="state"
                      value={profileForm.state}
                      onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                      placeholder="CO"
                      className="bg-navy-950 border-slate-700 text-white"
                      data-testid="profile-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code" className="text-slate-300">
                      ZIP Code
                    </Label>
                    <Input
                      id="zip_code"
                      value={profileForm.zip_code}
                      onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                      placeholder="80202"
                      className="bg-navy-950 border-slate-700 text-white"
                      data-testid="profile-zip-code"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isUpdatingProfile}
              className="w-full bg-primary hover:bg-primary/90 text-white"
              data-testid="save-profile-btn"
            >
              {isUpdatingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </form>

          {/* Password Section */}
          <div className="border-t border-slate-700 pt-4 mt-4">
            {!showPasswordSection ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordSection(true)}
                className="w-full border-slate-700 text-slate-300 hover:text-white"
                data-testid="change-password-toggle"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <p className="text-slate-400 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Change Password
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="current_password" className="text-slate-300">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    required
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="current-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-slate-300">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    required
                    minLength={8}
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password" className="text-slate-300">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    required
                    className="bg-navy-950 border-slate-700 text-white"
                    data-testid="confirm-password"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
                    }}
                    className="flex-1 border-slate-700 text-slate-300 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    data-testid="submit-password-btn"
                  >
                    {isChangingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Future Sections Placeholder */}
          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-500 text-xs text-center">
              Payment methods and billing summaries coming soon
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Info Modal */}
      <ServiceInfoModal
        serviceId={activeServiceId}
        isOpen={isServiceModalOpen}
        onClose={handleCloseServiceModal}
        pricingVisible={pricingEligible}
        dynamicPrices={dynamicPrices}
        showPurchaseCTA={showPurchaseCTA}
        onPurchase={handlePurchase}
        isPurchasing={isPurchasing}
      />

      {/* Ebook Promo Modal */}
      <EbookPromoModal
        open={showEbookModal}
        onOpenChange={setShowEbookModal}
        serId={activeEngagement?.ser?.id}
      />
    </div>
  );
};

/**
 * EbookOfferCard - Displays the ebook CTA for qualified users
 * Only shown after evaluation + onboarding completion
 */
const EbookOfferCard = ({ serId, onOpenModal, onTrackOffer }) => {
  const hasTracked = useRef(false);

  // Track offer shown on mount
  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      onTrackOffer?.();
    }
  }, [onTrackOffer]);

  const handleClick = async () => {
    // Track modal opened
    try {
      await trackEbookEvent("ebook_modal_opened", serId);
    } catch (err) {
      console.error("Failed to track ebook modal opened:", err);
    }
    onOpenModal();
  };

  const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_ai-value-pricing/artifacts/fnse5idx_AI_Survival_Guide_For_Humans_Front_Cover_Small.jpg";

  return (
    <div className="glass p-6 rounded-xl mb-6 border border-primary/20 bg-gradient-to-br from-navy-900 to-navy-950" data-testid="ebook-offer-card">
      <div className="flex items-start gap-5">
        {/* Book Cover */}
        <img 
          src={BOOK_COVER_URL} 
          alt="The A.I. Survival Guide For Humans" 
          className="w-24 h-auto rounded-lg shadow-lg flex-shrink-0"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading text-lg font-semibold text-white">
              Your Complimentary Resource
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
              Available
            </span>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Thank you for completing your evaluation. As promised, here&apos;s your free ebook to help you 
            understand AI, avoid common traps, and make better decisions with confidence.
          </p>
          <Button
            onClick={handleClick}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="ebook-cta-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Get Your Free AI Survival Guide (Ebook)
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserPortal;
